import fullLogo from "@/assets/full-logo.jpeg";
import mineralLogoMark from "@/assets/mineral-logo-mark.png";
import { cn } from "@/shared/lib/utils";

const markHeights = {
  sm: "h-8",
  md: "h-14",
  lg: "h-24",
  xl: "h-32",
} as const;

const fullWidths = {
  sm: "w-36",
  md: "w-44",
  lg: "w-56",
  xl: "w-72",
} as const;

const fullFrameSizes = {
  sm: "w-36 aspect-[1040/763]",
  md: "w-44 aspect-[1040/763]",
  lg: "w-56 aspect-[1040/763]",
  xl: "w-72 aspect-[1040/763]",
} as const;

const markFrameSizes = {
  sm: "h-8 aspect-[758/1144]",
  md: "h-14 aspect-[758/1144]",
  lg: "h-24 aspect-[758/1144]",
  xl: "h-32 aspect-[758/1144]",
} as const;

type Props = {
  size?: keyof typeof markHeights;
  /** `mark` = icon only; `full` = MINERAL COFFEE wordmark */
  variant?: "mark" | "full";
  /** Card frame — logo fills edge-to-edge with object-cover */
  framed?: boolean;
  /** Off-white tile behind mark — for dark/colored surfaces (e.g. sidebar) */
  backing?: "none" | "light";
  className?: string;
};

export function AppLogo({
  size = "md",
  variant = "mark",
  framed = false,
  backing = "none",
  className,
}: Props) {
  const isFull = variant === "full";

  const image = (
    <img
      src={isFull ? fullLogo : mineralLogoMark}
      alt="Mineral Coffee"
      draggable={false}
      className={cn(
        framed || backing === "light"
          ? "size-full object-cover"
          : cn(
              "max-w-full object-contain",
              isFull ? cn(fullWidths[size], "h-auto") : cn(markHeights[size], "w-auto"),
            ),
      )}
    />
  );

  if (!framed) {
    if (backing !== "light") {
      return (
        <div className={cn("inline-flex shrink-0 items-center justify-center", className)}>
          {image}
        </div>
      );
    }

    const tileSize = {
      sm: "size-12",
      md: "size-16",
      lg: "size-20",
      xl: "size-24",
    } as const;

    return (
      <div
        className={cn(
          "inline-flex shrink-0 overflow-hidden",
          tileSize[size],
          "bg-mineral-off-white rounded-xl",
          "shadow-sm ring-1 ring-mineral-light-gray/80",
          className,
        )}
      >
        {image}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "inline-flex shrink-0 overflow-hidden",
        isFull ? fullFrameSizes[size] : markFrameSizes[size],
        "rounded-[1.75rem] border border-mineral-light-gray/90",
        "shadow-[0_12px_40px_-16px] shadow-mineral-navy/20",
        "ring-1 ring-mineral-gold/30",
        className,
      )}
    >
      {image}
    </div>
  );
}
